/**
 * DEMO DATA - reports and analytics
 * ---------------------------------------------------------------------------
 * Fixture content for the report and analytics screens. Everything invented
 * lives here so it is obvious what is real and what is not; each page takes the
 * same shapes an endpoint would return.
 */

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKS = ['W36', 'W37', 'W38', 'W39', 'W40', 'W41', 'W42', 'W43'];
const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];

/* ------------------------------------------------------------ daily report */

export const DAILY_REPORT = {
  generatedAt: '06:42',
  readTime: '4 min',
  sources: 7,
  rowsRead: 214880,
  confidence: 86,
  headline:
    'Revenue is tracking 8% above plan, but margin slipped for the third week running — discounting on enterprise deals is the driver, not cost.',
  kpis: [
    { label: 'Revenue QTD', value: '$14.8M', pip: '92% to plan', dir: '', color: 'var(--blue)', icon: 'money' },
    { label: 'Gross margin', value: '37.6', unit: '%', pip: '▼ 3.6 pts', dir: 'dn', color: 'var(--red)', icon: 'chart' },
    { label: 'Cash', value: '$8.1M', sub: '14 months runway', color: 'var(--green)', icon: 'money' },
    { label: 'Open pipeline', value: '$42M', pip: '▲ 18%', dir: 'up', color: 'var(--purple)', icon: 'target' },
    { label: 'Headcount', value: '612', pip: '▲ 4', dir: 'up', color: 'var(--teal)', icon: 'people' },
  ],
  sections: [
    {
      tone: 'r',
      kind: 'Needs a decision',
      title: 'Enterprise discounting is running 6 points above plan',
      body: 'Average discount on deals above $250K reached 19% against a 13% plan. Fourteen deals cleared the deal-desk threshold without a recorded approval, nine of them in the final fortnight of the quarter. At this rate Q3 margin lands near 36%.',
      evidence: 'Salesforce · SAP',
      metric: 'GM-01',
    },
    {
      tone: 'a',
      kind: 'Watch',
      title: 'Cairo Fintech Group is 96 days overdue',
      body: '$1.2M outstanding and no payment plan recorded in any system. The 13-week cash model still assumes collection in November, which is the single largest assumption in the current forecast.',
      evidence: 'SAP · Email',
      metric: 'CASH-02',
    },
    {
      tone: 'g',
      kind: 'Opportunity',
      title: 'KSA pipeline crossed the $12M threshold',
      body: 'Pipeline reached $14.2M, up 62% year on year, passing the trigger your 2027 strategy set for opening a local presence. Win rate in the region holds at 31% against 28% group-wide.',
      evidence: 'Salesforce · SharePoint',
      metric: 'PIPE-01',
    },
    {
      tone: 'b',
      kind: 'For information',
      title: 'Two systems disagree on Q2 revenue by $180K',
      body: 'Salesforce counts two deals as Closed Won that SAP has not yet invoiced. The certified definition is invoiced net of credit notes, so the reported figure follows SAP. No action needed, but the board pack should cite SAP.',
      evidence: 'Salesforce · SAP',
      metric: 'REV-01',
    },
  ],
  revenueTrend: [
    { week: 'W36', actual: 1.6, plan: 1.7 },
    { week: 'W37', actual: 1.9, plan: 1.7 },
    { week: 'W38', actual: 1.8, plan: 1.8 },
    { week: 'W39', actual: 2.1, plan: 1.8 },
    { week: 'W40', actual: 2.0, plan: 1.9 },
    { week: 'W41', actual: 2.3, plan: 1.9 },
    { week: 'W42', actual: 2.2, plan: 2.0 },
    { week: 'W43', actual: 2.4, plan: 2.0 },
  ],
  marginTrend: [
    { week: 'W36', margin: 41.2 },
    { week: 'W37', margin: 40.8 },
    { week: 'W38', margin: 40.1 },
    { week: 'W39', margin: 39.4 },
    { week: 'W40', margin: 38.9 },
    { week: 'W41', margin: 38.2 },
    { week: 'W42', margin: 37.9 },
    { week: 'W43', margin: 37.6 },
  ],
  contributing: [
    { source: 'SAP S/4HANA', rows: 148210, share: 69 },
    { source: 'Salesforce', rows: 42904, share: 20 },
    { source: 'Workday HR', rows: 12880, share: 6 },
    { source: 'Dynamics 365', rows: 7420, share: 3 },
    { source: 'Documents', rows: 3466, share: 2 },
  ],
};

/* ---------------------------------------------------------- report history */

export const REPORT_HISTORY = [
  { id: 'R-1042', date: 'Today · 06:42', type: 'Daily', title: 'Margin slipped for a third week', sources: 7, confidence: 86, status: 'Delivered', tone: 'g', readers: 9 },
  { id: 'R-1041', date: 'Yesterday · 06:41', type: 'Daily', title: 'KSA pipeline crossed the threshold', sources: 7, confidence: 88, status: 'Delivered', tone: 'g', readers: 11 },
  { id: 'R-1040', date: 'Fri · 06:40', type: 'Daily', title: 'Collections improved, freight still elevated', sources: 6, confidence: 81, status: 'Delivered', tone: 'g', readers: 8 },
  { id: 'R-1039', date: 'Fri · 17:02', type: 'Ad-hoc', title: 'Q3 discount exposure by region', sources: 3, confidence: 74, status: 'Delivered', tone: 'g', readers: 4 },
  { id: 'R-1038', date: 'Thu · 06:42', type: 'Daily', title: 'Two systems disagree on Q2 revenue', sources: 7, confidence: 79, status: 'Flagged', tone: 'a', readers: 12 },
  { id: 'R-1037', date: 'Wed · 06:41', type: 'Daily', title: 'Cairo Fintech passes 90 days overdue', sources: 7, confidence: 84, status: 'Delivered', tone: 'g', readers: 10 },
  { id: 'R-1036', date: 'Tue · 06:43', type: 'Daily', title: 'Headcount plan on track, attrition flat', sources: 6, confidence: 90, status: 'Delivered', tone: 'g', readers: 7 },
  { id: 'R-1035', date: 'Mon · 06:40', type: 'Weekly', title: 'Week 42 executive summary', sources: 7, confidence: 87, status: 'Delivered', tone: 'g', readers: 18 },
  { id: 'R-1034', date: 'Sun · 06:42', type: 'Daily', title: 'Pipeline coverage thinning in EMEA', sources: 5, confidence: 68, status: 'Low evidence', tone: 'r', readers: 5 },
];

export const REPORT_VOLUME = WEEKS.map((week, i) => ({
  week,
  daily: 7,
  adhoc: [2, 4, 3, 5, 4, 6, 5, 7][i],
}));

/* ----------------------------------------------------- executive dashboard */

export const EXEC = {
  kpis: [
    { label: 'Revenue QTD', value: '$14.8M', pip: '▲ 8% vs plan', dir: 'up', color: 'var(--blue)', icon: 'money' },
    { label: 'Gross margin', value: '37.6', unit: '%', pip: '▼ 3.6 pts', dir: 'dn', color: 'var(--red)', icon: 'chart' },
    { label: 'Net revenue retention', value: '112', unit: '%', pip: '▲ 4 pts', dir: 'up', color: 'var(--green)', icon: 'target' },
    { label: 'Cash runway', value: '14', unit: ' mo', color: 'var(--teal)', icon: 'clock' },
  ],
  revenueByMonth: MONTHS.map((month, i) => ({
    month,
    revenue: [4.1, 4.4, 4.9, 4.6, 5.2, 5.6][i],
    cogs: [2.4, 2.6, 2.9, 2.8, 3.2, 3.5][i],
  })),
  pipelineByRegion: [
    { region: 'Egypt', value: 14.8 },
    { region: 'KSA', value: 14.2 },
    { region: 'UAE', value: 7.4 },
    { region: 'Levant', value: 3.6 },
    { region: 'Other GCC', value: 2.0 },
  ],
  marginByLine: [
    { line: 'Software', margin: 61.2 },
    { line: 'Services', margin: 34.8 },
    { line: 'Hardware', margin: 18.4 },
    { line: 'Support', margin: 47.1 },
  ],
  topAccounts: [
    { account: 'Nile Holding', revenue: '$2.4M', margin: '42%', change: '▲ 12%', tone: 'g' },
    { account: 'Delta Manufacturing', revenue: '$1.9M', margin: '31%', change: '▲ 4%', tone: 'g' },
    { account: 'Cairo Fintech Group', revenue: '$1.6M', margin: '28%', change: '▼ 9%', tone: 'r' },
    { account: 'Gulf Logistics', revenue: '$1.2M', margin: '36%', change: '▲ 21%', tone: 'g' },
    { account: 'Red Sea Retail', revenue: '$0.9M', margin: '24%', change: '▼ 3%', tone: 'a' },
  ],
};

/* -------------------------------------------------------- datasource health */

export const HEALTH = {
  kpis: [
    { label: 'Sources healthy', value: '4/7', color: 'var(--green)', icon: 'check' },
    { label: 'Sync success rate', value: '96.4', unit: '%', pip: '▲ 1.2 pts', dir: 'up', color: 'var(--blue)', icon: 'sync' },
    { label: 'Median latency', value: '418', unit: ' ms', color: 'var(--purple)', icon: 'clock' },
    { label: 'Failed syncs (7d)', value: '12', color: 'var(--amber)', icon: 'warn', sub: '9 from one source' },
  ],
  latency: DAYS.map((day, i) => ({
    day,
    salesforce: [360, 372, 381, 358, 402, 388, 380][i],
    sap: [498, 512, 530, 505, 545, 520, 512][i],
    workday: [610, 634, 640, 628, 655, 641, 640][i],
  })),
  syncOutcomes: DAYS.map((day, i) => ({
    day,
    success: [41, 44, 43, 45, 42, 40, 44][i],
    failed: [1, 0, 2, 1, 4, 3, 1][i],
  })),
  sources: [
    { name: 'Salesforce Production', uptime: 99.8, success: 99.2, latency: 381, errors: 3, last: '7m ago', state: 'Healthy', tone: 'g' },
    { name: 'SAP ERP', uptime: 99.4, success: 98.1, latency: 512, errors: 0, last: '18m ago', state: 'Healthy', tone: 'g' },
    { name: 'Workday HR', uptime: 99.9, success: 100, latency: 640, errors: 0, last: '1h ago', state: 'Healthy', tone: 'g' },
    { name: 'Dynamics 365 (GCC)', uptime: 98.2, success: 96.4, latency: 720, errors: 1, last: '16m ago', state: 'Healthy', tone: 'g' },
    { name: 'Snowflake warehouse', uptime: 99.1, success: 94.0, latency: 241, errors: 2, last: '8m ago', state: 'Syncing', tone: 'b' },
    { name: 'Odoo Egypt', uptime: 62.0, success: 0, latency: 0, errors: 12, last: '3h ago', state: 'Auth failed', tone: 'r' },
    { name: 'Billing REST API', uptime: 0, success: 0, latency: 0, errors: 0, last: 'Never', state: 'Not connected', tone: 'n' },
  ],
};

/* --------------------------------------------------------- search analytics */

export const SEARCH = {
  kpis: [
    { label: 'Questions asked', value: '1,284', pip: '▲ 22%', dir: 'up', color: 'var(--blue)', icon: 'chat' },
    { label: 'Answered with evidence', value: '87', unit: '%', color: 'var(--green)', icon: 'check' },
    { label: 'Median answer time', value: '4.2', unit: ' s', color: 'var(--purple)', icon: 'clock' },
    { label: 'No evidence held', value: '9', unit: '%', color: 'var(--amber)', icon: 'warn', sub: 'refused to guess' },
  ],
  volume: WEEKS.map((week, i) => ({
    week,
    questions: [612, 704, 688, 810, 902, 988, 1120, 1284][i],
  })),
  intents: [
    { intent: 'Lookup', share: 41 },
    { intent: 'Diagnosis', share: 27 },
    { intent: 'Forecast', share: 18 },
    { intent: 'Decision', share: 14 },
  ],
  topQuestions: [
    { q: 'Why did gross margin drop last quarter?', asked: 84, answered: '92%', latency: '4.4s', tone: 'g' },
    { q: 'How much cash will we have at the end of Q4?', asked: 71, answered: '88%', latency: '5.1s', tone: 'g' },
    { q: 'Top overdue customers', asked: 64, answered: '96%', latency: '2.8s', tone: 'g' },
    { q: 'Pipeline by region this quarter', asked: 58, answered: '94%', latency: '3.2s', tone: 'g' },
    { q: 'Competitor pricing in KSA', asked: 22, answered: '0%', latency: '1.1s', tone: 'r' },
    { q: 'Headcount cost by department', asked: 19, answered: '74%', latency: '6.0s', tone: 'a' },
  ],
  gaps: [
    { q: 'Competitor price points', reason: 'Not held in any connected system', asked: 22 },
    { q: 'Office and lease costs by city', reason: 'Not held in any connected system', asked: 11 },
    { q: 'Local salary benchmarks', reason: 'Needs an HR data feed', asked: 8 },
  ],
};

/* ----------------------------------------------------------- trend analysis */

export const TRENDS = {
  kpis: [
    { label: 'Metrics tracked', value: '64', color: 'var(--blue)', icon: 'chart' },
    { label: 'Trending down', value: '7', color: 'var(--red)', icon: 'warn', sub: 'beyond normal range' },
    { label: 'Trending up', value: '12', color: 'var(--green)', icon: 'target' },
    { label: 'Stable', value: '45', color: 'var(--muted-2)', icon: 'check' },
  ],
  marginVsFreight: MONTHS.map((month, i) => ({
    month,
    margin: [41.6, 41.2, 40.4, 39.1, 38.2, 37.6][i],
    freight: [2.1, 2.2, 2.4, 2.8, 3.1, 3.2][i],
  })),
  rows: [
    { metric: 'Gross margin', now: '37.6%', prior: '41.2%', change: -3.6, dir: 'dn', since: '6 weeks', spark: [41.6, 41.2, 40.4, 39.4, 38.2, 37.6], note: 'Driven by discounting, not cost' },
    { metric: 'Inbound freight per unit', now: '$3.20', prior: '$2.10', change: 52.4, dir: 'up', since: '5 months', spark: [2.1, 2.2, 2.4, 2.8, 3.1, 3.2], note: 'Carrier contract renews in Nov' },
    { metric: 'Qualified pipeline', now: '$42M', prior: '$35.6M', change: 18.0, dir: 'up', since: '1 quarter', spark: [33, 34, 36, 38, 40, 42], note: 'Concentrated in 11 deals' },
    { metric: 'Net revenue retention', now: '112%', prior: '108%', change: 3.7, dir: 'up', since: '2 quarters', spark: [104, 106, 107, 108, 110, 112], note: 'Upsell in the Egypt base' },
    { metric: 'Days sales outstanding', now: '68 days', prior: '54 days', change: 25.9, dir: 'up', since: '3 months', spark: [54, 57, 60, 63, 66, 68], note: 'One account is half the increase' },
    { metric: 'Average deal size', now: '$460K', prior: '$448K', change: 2.7, dir: 'up', since: '2 quarters', spark: [441, 445, 448, 452, 456, 460], note: 'Within normal variation' },
  ],
};

/* -------------------------------------------------------- anomaly detection */

export const ANOMALIES = {
  kpis: [
    { label: 'Open anomalies', value: '5', color: 'var(--red)', icon: 'warn' },
    { label: 'Detected this week', value: '11', color: 'var(--amber)', icon: 'spark' },
    { label: 'Confirmed real', value: '73', unit: '%', color: 'var(--green)', icon: 'check', sub: 'of those reviewed' },
    { label: 'Median time to flag', value: '38', unit: ' min', color: 'var(--purple)', icon: 'clock' },
  ],
  detection: DAYS.map((day, i) => ({
    day,
    expected: [100, 102, 101, 103, 104, 60, 58][i],
    observed: [99, 104, 101, 118, 142, 61, 57][i],
  })),
  rows: [
    { severity: 'Serious', tone: 'r', metric: 'Discount rate, enterprise', observed: '19.0%', expected: '12.5% ± 1.8', deviation: '+3.6σ', source: 'Salesforce', when: '2h ago', status: 'Open' },
    { severity: 'Serious', tone: 'r', metric: 'Days sales outstanding', observed: '68 days', expected: '55 ± 4', deviation: '+3.2σ', source: 'SAP', when: '6h ago', status: 'Open' },
    { severity: 'Warning', tone: 'a', metric: 'Inbound freight per unit', observed: '$3.20', expected: '$2.40 ± 0.30', deviation: '+2.6σ', source: 'SAP', when: 'Yesterday', status: 'Acknowledged' },
    { severity: 'Warning', tone: 'a', metric: 'Odoo sync failures', observed: '12 / day', expected: '0 ± 1', deviation: '+9.0σ', source: 'Odoo Egypt', when: 'Yesterday', status: 'Open' },
    { severity: 'Warning', tone: 'a', metric: 'Support ticket volume', observed: '412', expected: '350 ± 30', deviation: '+2.1σ', source: 'Salesforce', when: '2 days ago', status: 'Open' },
    { severity: 'Resolved', tone: 'g', metric: 'Payroll run total', observed: '$1.94M', expected: '$1.90M ± 0.05', deviation: '+0.8σ', source: 'Workday', when: '3 days ago', status: 'Explained — bonus cycle' },
  ],
};

/* ---------------------------------------------------- document intelligence */

export const DOCS = {
  kpis: [
    { label: 'Documents indexed', value: '38,412', pip: '▲ 1,204', dir: 'up', color: 'var(--blue)', icon: 'file' },
    { label: 'Pages extracted', value: '412K', color: 'var(--purple)', icon: 'db' },
    { label: 'Extraction accuracy', value: '94', unit: '%', color: 'var(--green)', icon: 'target' },
    { label: 'Needs review', value: '186', color: 'var(--amber)', icon: 'warn', sub: 'low-confidence extractions' },
  ],
  byType: [
    { type: 'Contracts', count: 12480 },
    { type: 'Invoices', count: 9860 },
    { type: 'Board packs', count: 6140 },
    { type: 'Policies', count: 5320 },
    { type: 'Spreadsheets', count: 4612 },
  ],
  ingestion: WEEKS.map((week, i) => ({
    week,
    indexed: [820, 910, 1040, 980, 1180, 1120, 1260, 1204][i],
  })),
  extracted: [
    { field: 'Contract value', found: 11204, coverage: 90, tone: 'g' },
    { field: 'Renewal date', found: 10880, coverage: 87, tone: 'g' },
    { field: 'Price-change clause', found: 3140, coverage: 25, tone: 'a' },
    { field: 'Termination notice', found: 9740, coverage: 78, tone: 'g' },
    { field: 'Liability cap', found: 2410, coverage: 19, tone: 'r' },
  ],
  recent: [
    { name: 'Strategy_2027.pdf', type: 'Board pack', pages: 64, added: '2h ago', status: 'Indexed', tone: 'g' },
    { name: 'Budget_FY26_v8.xlsx', type: 'Spreadsheet', pages: 18, added: '5h ago', status: 'Uncertified', tone: 'a' },
    { name: 'NileHolding_MSA.pdf', type: 'Contract', pages: 41, added: 'Yesterday', status: 'Indexed', tone: 'g' },
    { name: 'Freight_Contract_2026.pdf', type: 'Contract', pages: 22, added: 'Yesterday', status: 'Needs review', tone: 'r' },
    { name: 'Q2_Board_Minutes.docx', type: 'Board pack', pages: 12, added: '2 days ago', status: 'Indexed', tone: 'g' },
  ],
};

/* ------------------------------------------------------------ user adoption */

export const ADOPTION = {
  kpis: [
    { label: 'Weekly active users', value: '148', pip: '▲ 19%', dir: 'up', color: 'var(--blue)', icon: 'people' },
    { label: 'Of licensed seats', value: '61', unit: '%', color: 'var(--green)', icon: 'target' },
    { label: 'Questions per user', value: '8.7', pip: '▲ 1.4', dir: 'up', color: 'var(--purple)', icon: 'chat' },
    { label: 'Dormant 30 days', value: '34', color: 'var(--amber)', icon: 'warn', sub: 'seats to reclaim' },
  ],
  activity: WEEKS.map((week, i) => ({
    week,
    active: [78, 84, 96, 104, 118, 126, 139, 148][i],
    returning: [52, 58, 66, 74, 86, 94, 108, 119][i],
  })),
  byDepartment: [
    { department: 'Finance', users: 42 },
    { department: 'Sales', users: 38 },
    { department: 'Operations', users: 26 },
    { department: 'Executive', users: 18 },
    { department: 'HR', users: 14 },
    { department: 'Other', users: 10 },
  ],
  features: [
    { feature: 'Ask a question', usage: 96, tone: 'g' },
    { feature: 'Daily report', usage: 74, tone: 'g' },
    { feature: 'Document upload', usage: 41, tone: 'a' },
    { feature: 'Report sharing', usage: 33, tone: 'a' },
    { feature: 'Voice input', usage: 12, tone: 'r' },
  ],
  roles: [
    { role: 'Executive', seats: 24, active: 18, adoption: 75, questions: 312, tone: 'g' },
    { role: 'Finance', seats: 58, active: 42, adoption: 72, questions: 486, tone: 'g' },
    { role: 'Sales', seats: 72, active: 38, adoption: 53, questions: 298, tone: 'a' },
    { role: 'Operations', seats: 48, active: 26, adoption: 54, questions: 142, tone: 'a' },
    { role: 'HR', seats: 40, active: 14, adoption: 35, questions: 46, tone: 'r' },
  ],
};

/* ---------------------------------------------------------- report analytics */

export const REPORT_ANALYTICS = {
  kpis: [
    { label: 'Reports generated', value: '1,042', pip: '▲ 14%', dir: 'up', color: 'var(--blue)', icon: 'file' },
    { label: 'Opened within 1h', value: '68', unit: '%', color: 'var(--green)', icon: 'clock' },
    { label: 'Median confidence', value: '85', unit: '%', color: 'var(--purple)', icon: 'target' },
    { label: 'Flagged low evidence', value: '38', color: 'var(--amber)', icon: 'warn', sub: '3.6% of reports' },
  ],
  delivery: WEEKS.map((week, i) => ({
    week,
    generated: [84, 92, 88, 104, 112, 118, 126, 134][i],
    opened: [62, 70, 68, 82, 92, 96, 108, 118][i],
  })),
  confidenceSpread: [
    { band: '90-100%', reports: 284 },
    { band: '80-89%', reports: 412 },
    { band: '70-79%', reports: 218 },
    { band: '60-69%', reports: 90 },
    { band: 'Below 60%', reports: 38 },
  ],
  sections: [
    { section: 'Needs a decision', reads: 92, actions: 41, tone: 'g' },
    { section: 'Watch', reads: 78, actions: 22, tone: 'g' },
    { section: 'Opportunity', reads: 64, actions: 18, tone: 'a' },
    { section: 'For information', reads: 38, actions: 3, tone: 'r' },
  ],
  readers: [
    { name: 'John Smith', role: 'CEO', opened: 42, of: 44, acted: 12 },
    { name: 'Salma Adel', role: 'CFO', opened: 44, of: 44, acted: 19 },
    { name: 'Yara Rostom', role: 'CRO', opened: 38, of: 44, acted: 9 },
    { name: 'Omar Shafik', role: 'Controller', opened: 31, of: 44, acted: 14 },
    { name: 'Hisham Fouad', role: 'CHRO', opened: 18, of: 44, acted: 2 },
  ],
};
